import React, { useState } from 'react';
import { Modal, Button, Row, Col, Badge, Table, Spinner } from 'react-bootstrap';
import { addToCart } from '../../../services/partsTech';

export default function PartsTechDetailModal({ part, onHide, onSelectPart, resolvedVehicle, vehicleVin }) {
    const [imgIndex, setImgIndex]       = useState(0);
    const [addingToCart, setAddingToCart] = useState(false);
    const [cartMsg, setCartMsg]         = useState('');

    if (!part) return null;

    async function handleAddToCart() {
        if (!part.partNumberId || !part.lineCardId || !part.accountId || !part.storeId) {
            setCartMsg('Missing cart info — search again to refresh part data.');
            return;
        }
        setAddingToCart(true);
        setCartMsg('');
        try {
            await addToCart({
                partNumberId: part.partNumberId,
                partNumber:   part.partNumber,
                lineCardId:   part.lineCardId,
                accountId:    part.accountId,
                storeId:      part.storeId,
                quantity:     1,
                partName:     part.description,
                vehicleId:    resolvedVehicle?.id,
                vin:          vehicleVin,
            });
            setCartMsg('Added to cart!');
        } catch (e) {
            setCartMsg(e?.response?.data?.error || 'Failed to add to cart');
        } finally {
            setAddingToCart(false);
        }
    }

    const images = part.images || [];
    const currentImage = images[imgIndex];

    // Find specific description blocks
    const productDesc   = part.descriptions?.find(d => d.code === 'DES');
    const appSummary    = part.descriptions?.find(d => d.code === 'APS');
    const otherDescs    = part.descriptions?.filter(d => d.code !== 'DES' && d.code !== 'APS') || [];

    function handleSelect() {
        onSelectPart({
            partNumber:  part.partNumber,
            description: part.description,
            cost:        part.cost,
            list:        part.list,
            distributor: part.supplier,
        });
        onHide();
    }

    return (
        <Modal show={!!part} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    <strong>{part.partNumber}</strong>
                    <span className="text-muted ml-2" style={{ fontSize: '0.85em' }}>{part.description}</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row>
                    {/* Left — image */}
                    <Col xs={12} md={4}>
                        {images.length > 0 ? (
                            <div style={{ textAlign: 'center' }}>
                                <img
                                    src={currentImage.medium || currentImage.full}
                                    alt={part.description}
                                    style={{ maxWidth: '100%', maxHeight: '280px', objectFit: 'contain', border: '1px solid #eee', borderRadius: 4, padding: 8 }}
                                />
                                {images.length > 1 && (
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                                        {images.map((img, i) => (
                                            <img
                                                key={i}
                                                src={img.preview}
                                                alt=""
                                                onClick={() => setImgIndex(i)}
                                                style={{
                                                    width: 52, height: 52, objectFit: 'contain', cursor: 'pointer',
                                                    border: i === imgIndex ? '2px solid #007bff' : '1px solid #ddd',
                                                    borderRadius: 3, padding: 2,
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ height: 200, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, color: '#aaa' }}>
                                No image available
                            </div>
                        )}

                        {/* Pricing summary */}
                        <Table size="sm" bordered style={{ marginTop: 16 }}>
                            <tbody>
                                <tr><td><strong>Brand</strong></td><td>{part.brand}</td></tr>
                                <tr><td><strong>Supplier</strong></td><td>{part.supplier}</td></tr>
                                <tr><td><strong>Cost</strong></td><td>${part.cost.toFixed(2)}</td></tr>
                                {part.list > 0 && (
                                    <tr>
                                        <td><strong>MSRP</strong></td>
                                        <td><span className="text-muted">${part.list.toFixed(2)}</span></td>
                                    </tr>
                                )}
                                {part.coreCharge > 0 && (
                                    <tr><td><strong>Core</strong></td><td>${part.coreCharge.toFixed(2)}</td></tr>
                                )}
                                <tr>
                                    <td><strong>Home Store</strong></td>
                                    <td>
                                        {part.availability > 0
                                            ? <Badge variant="success">{part.availability} in stock</Badge>
                                            : <Badge variant="danger">Out of stock</Badge>}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>

                    {/* Right — details */}
                    <Col xs={12} md={8}>
                        {/* Product description */}
                        {productDesc?.values?.[0] && (
                            <div style={{ marginBottom: 12 }}>
                                <h6>Description</h6>
                                <p style={{ color: '#444' }}>{productDesc.values[0]}</p>
                            </div>
                        )}

                        {/* Marketing descriptions */}
                        {part.marketingDescriptions?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <h6>Features</h6>
                                <ul style={{ paddingLeft: 18, color: '#444' }}>
                                    {part.marketingDescriptions.map((d, i) => <li key={i}>{d}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Notes */}
                        {part.notes?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <h6>Notes</h6>
                                <ul style={{ paddingLeft: 18, color: '#444' }}>
                                    {part.notes.map((n, i) => <li key={i}>{n}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* Attributes */}
                        {part.attributes?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <h6>Specifications</h6>
                                <Table size="sm" bordered>
                                    <tbody>
                                        {part.attributes.map((a, i) => (
                                            <tr key={i}>
                                                <td style={{ width: '40%', fontWeight: 600, background: '#f8f9fa' }}>{a.name}</td>
                                                <td>{Array.isArray(a.values) ? a.values.join(', ') : a.values}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Extended info */}
                        {part.extendedInfo?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <h6>Additional Information</h6>
                                <Table size="sm" bordered>
                                    <tbody>
                                        {part.extendedInfo.map((e, i) => (
                                            <tr key={i}>
                                                <td style={{ width: '40%', fontWeight: 600, background: '#f8f9fa' }}>{e.name}</td>
                                                <td>{e.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Store availability breakdown */}
                        {part.stores?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <h6>Store Availability</h6>
                                <Table size="sm" bordered>
                                    <thead>
                                        <tr>
                                            <th>Store</th>
                                            <th>Qty</th>
                                            <th>Type</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {part.stores.map((s, i) => (
                                            <tr key={i}>
                                                <td>{s.name}</td>
                                                <td>
                                                    {s.quantity > 0
                                                        ? <Badge variant="success">{s.quantity}</Badge>
                                                        : <Badge variant="secondary">0</Badge>}
                                                </td>
                                                <td><small className="text-muted">{s.type}</small></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Application summary — collapsible, often very long */}
                        {appSummary?.values?.[0] && (
                            <details style={{ marginBottom: 12 }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#555' }}>
                                    Application Summary
                                </summary>
                                <p style={{ color: '#555', marginTop: 8, fontSize: '0.85em' }}>
                                    {appSummary.values[0]}
                                </p>
                            </details>
                        )}

                        {/* Any other description blocks */}
                        {otherDescs.map((d, i) => (
                            d.values?.length > 0 && (
                                <details key={i} style={{ marginBottom: 8 }}>
                                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#555' }}>{d.label}</summary>
                                    <ul style={{ paddingLeft: 18, marginTop: 8, color: '#555', fontSize: '0.85em' }}>
                                        {d.values.map((v, j) => <li key={j}>{v}</li>)}
                                    </ul>
                                </details>
                            )
                        ))}
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                {cartMsg && (
                    <span style={{ marginRight: 'auto', fontSize: '0.85em', color: cartMsg.includes('!') ? 'green' : 'red' }}>
                        {cartMsg}
                    </span>
                )}
                <Button variant="secondary" onClick={onHide}>Close</Button>
                <Button
                    variant="outline-primary"
                    disabled={addingToCart}
                    onClick={handleAddToCart}
                    title="Add to PartsTech cart for ordering"
                >
                    {addingToCart ? <Spinner animation="border" size="sm" /> : '🛒 Add to Cart'}
                </Button>
                <Button variant="success" onClick={handleSelect}>
                    + Invoice (Cost: ${part.cost.toFixed(2)})
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
